import React, { Fragment, lazy, Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';
import AuthGuard from 'src/components/AuthGuard';
import CrossAppRedirect from 'src/components/CrossAppRedirect';
import LoadingScreen from 'src/components/LoadingScreen';
import routeSplitMap from 'src/config/routeSplitMap.json';
import { buildExternalUrl, getContentBaseUrl, normalizeTargetApp, isContentTarget } from 'src/config/appTargets';
import DashboardLayout from 'src/layouts/DashboardLayout';
import { pathJoin } from 'src/utilities/Utilities';
import NotFoundView from 'src/views/errors/NotFoundView';
import useFeatureFlags from './hooks/useFeatureFlags';
import useAuth from './hooks/useAuth';
import {
  getProcessAiGroupName,
  FEATURE_FLAGS,
  FRONTEND_FEATURE_FLAGS,
  CONSOLE_ADMINS_GROUP,
  CONSOLE_EXPERIENCE_GROUP,
  HARVEST_EMPLOYEES_GROUP,
  MONDAY_ADMINS_GROUP,
  MONDAY_SMES_GROUP,
  CONSOLE_FINANCE_GROUP,
  CONSOLE_CLIENT_ATTRIBUTION_GROUP,
  CONSOLE_DEVELOPERS_GROUP,
  CONSOLE_GROWTH_GROUP,
} from 'src/constants';

const ROUTE_SPLIT_MAP = routeSplitMap?.routes || {};

const buildCompositeKey = (routeKey, parentCompositeKey) => {
  if (!routeKey) {
    return parentCompositeKey || '';
  }

  return parentCompositeKey ? `${parentCompositeKey}.${routeKey}` : routeKey;
};

const resolveSplitMetadata = (route, parent) => {
  const candidates = [];

  if (route?.key) {
    candidates.push(route.key);
  }

  if (parent?.key) {
    candidates.push(buildCompositeKey(route.key, parent.key));
  }

  if (parent?.splitCompositeKey) {
    candidates.push(buildCompositeKey(route.key, parent.splitCompositeKey));
  }

  for (const candidate of candidates) {
    if (candidate && ROUTE_SPLIT_MAP[candidate]) {
      return { key: candidate, metadata: ROUTE_SPLIT_MAP[candidate] };
    }
  }

  return { key: candidates[candidates.length - 1] || route.key, metadata: undefined };
};

const annotateSplitTarget = (route, parent) => {
  const { key: compositeKey, metadata } = resolveSplitMetadata(route, parent);
  const normalizedTarget = normalizeTargetApp(metadata?.targetApp);

  route.splitCompositeKey = compositeKey;
  route.splitTarget = normalizedTarget;
  route.splitTargetRaw = metadata?.targetApp || 'lanternal';

  if (isContentTarget(normalizedTarget)) {
    const baseUrl = getContentBaseUrl();

    if (baseUrl) {
      route.externalUrl = buildExternalUrl(baseUrl, route.path);
      route.component = CrossAppRedirect;
    } else {
      route.externalUrl = null;
    }
  } else {
    route.externalUrl = null;
  }

  return route;
};

export const getRoutes = (user, hasProcessAIPermission, featureFlags = {}) => {
  const processAiGroup = getProcessAiGroupName();
  const flattenRoutes = (routes, parent = {}) => {
    let flatRoutes = [];

    routes.forEach((route) => {
      let finalPath;
      if (route.path === '' && parent.path) {
        finalPath = parent.path;
      } else if (parent.path) {
        finalPath = pathJoin(parent.path, route.path);
      } else {
        finalPath = route.path;
      }

      const processedRoute = {
        ...route,
        path: finalPath,
        guard: route.guard || parent.guard,
        layout: route.layout || parent.layout,
      };

      processedRoute.accessible = isRouteAccessible(processedRoute, parent, user);
      annotateSplitTarget(processedRoute, parent);

      if (processedRoute.routes) {
        if (processedRoute.component && processedRoute.accessible) {
          flatRoutes.push({
            ...processedRoute,
            routes: undefined,
          });
        }
        const childRoutes = flattenRoutes(processedRoute.routes, processedRoute);
        flatRoutes.push(...childRoutes);
      } else if (processedRoute.accessible) {
        flatRoutes.push(processedRoute);
      }
    });

    return flatRoutes;
  };

  const processRoutes = (routes, parent = {}) => {
    return routes.map((route) => {
      route.path = pathJoin(parent.path, route.path);
      route.guard = route.guard || parent.guard;

      route.accessible = isRouteAccessible(route, parent, user);
      annotateSplitTarget(route, parent);

      if (route.routes) {
        route.routes = processRoutes(route.routes, route);
      }

      return route;
    });
  };

  const isRouteAccessible = (route, parent, user) => {
    const mondayRouteKeys = ['mondayWorkspaceApp'];
    const mondayManagerRouteKeys = ['mondayBatchSync'];
    const processAiRouteKeys = ['forecastingV2', 'weeklyCall'];

    if (route?.featureFlag && featureFlags[route.featureFlag] !== true) {
      return false;
    }

    if (mondayRouteKeys.includes(route?.key)) {
      return (
        user?.groups?.indexOf(MONDAY_ADMINS_GROUP) !== -1 ||
        user?.groups?.indexOf(CONSOLE_DEVELOPERS_GROUP) !== -1 ||
        user?.groups?.indexOf(CONSOLE_ADMINS_GROUP) !== -1 ||
        user?.groups?.indexOf(HARVEST_EMPLOYEES_GROUP) !== -1
      );
    }

    if (mondayManagerRouteKeys.includes(route?.key)) {
      return (
        user?.groups?.indexOf(MONDAY_ADMINS_GROUP) !== -1 ||
        user?.groups?.indexOf(MONDAY_SMES_GROUP) !== -1 ||
        user?.groups?.indexOf(CONSOLE_DEVELOPERS_GROUP) !== -1 ||
        user?.groups?.indexOf(CONSOLE_ADMINS_GROUP) !== -1 ||
        user?.groups?.indexOf(HARVEST_EMPLOYEES_GROUP) !== -1
      );
    }

    if (processAiRouteKeys.includes(route?.key)) {
      if (parent?.accessible === false) {
        return false;
      }

      if (user?.groups?.indexOf(CONSOLE_DEVELOPERS_GROUP) !== -1) {
        return true;
      }

      if (hasProcessAIPermission) {
        return hasProcessAIPermission();
      }

      return isUserInAnyGroup(user, route.groups);
    }

    if (user?.groups?.indexOf(CONSOLE_DEVELOPERS_GROUP) !== -1) {
      return true;
    }

    return isAccessible(parent, route, user);
  };

  const isAccessible = (parent, route, user) => {
    return parent?.accessible === false ? false : userHasAccessToRoute(route, user);
  };

  const userHasAccessToRoute = (route, user) => {
    return route.groups && route.groups.length ? isUserInAnyGroup(user, route.groups) : true;
  };

  const isUserInAnyGroup = (user, groups) => {
    return groups.some((group) => user?.groups?.indexOf(group) !== -1);
  };

  const objectify = (routes) => {
    let obj = {};

    routes.forEach((route) => {
      obj[route.key] = {
        route: {
          accessible: route.accessible,
          path: route.path,
          splitTarget: route.splitTarget,
          splitTargetRaw: route.splitTargetRaw,
          externalUrl: route.externalUrl,
          featureFlag: route.featureFlag,
        },
        ...(route.routes ? objectify(route.routes) : {}),
      };
    });

    return obj;
  };

  let routeDefinitions = [
    {
      key: 'login',
      path: '/login',
      component: lazy(() => import('src/views/auth/LoginView/index')),
    },
    {
      key: 'error404',
      path: '/404',
      component: NotFoundView,
    },
    {
      key: 'error403',
      path: '/403',
      component: lazy(() => import('src/views/errors/ForbiddenView')),
    },
    {
      key: 'timedOut',
      path: '/timed-out',
      component: lazy(() => import('src/views/errors/TimedOutView')),
    },
    {
      key: 'app',
      path: '/',
      guard: AuthGuard,
      layout: DashboardLayout,
      routes: [
        {
          key: 'home',
          path: '/',
          component: lazy(() => import('src/views/home/HomeView')),
        },
        {
          key: 'profile',
          path: '/profile',
          component: lazy(() => import('src/views/profile/index')),
          groups: [HARVEST_EMPLOYEES_GROUP],
        },
        {
          key: 'team',
          path: '/team',
          component: lazy(() => import('src/views/clientTeam/index')),
          groups: [HARVEST_EMPLOYEES_GROUP],
        },
        // {
        //   key: 'dataIntegrations',
        //   path: '/data-integrations',
        //   component: lazy(() => import('src/views/dataIntegrations/index')),
        //   groups: [HARVEST_EMPLOYEES_GROUP],
        // },
        {
          key: 'emberDatasetManager',
          path: '/ember-dataset-manager',
          component: lazy(() => import('src/views/emberDataManagementTools/emberDatasetManager/index')),
          groups: [HARVEST_EMPLOYEES_GROUP],
        },
        {
          key: 'clientGoalsTracker',
          path: '/client-goals-tracker',
          component: lazy(() => import('src/views/emberDataManagementTools/clientGoalsTracker/index')),
          groups: [HARVEST_EMPLOYEES_GROUP],
        },
        {
          key: 'harvesterStatus',
          path: '/harvester-status',
          component: lazy(() => import('src/views/harvesterStatus/index')),
          groups: [CONSOLE_DEVELOPERS_GROUP],
        },
        {
          key: 'mondayBatchSync',
          path: '/monday-batch-sync',
          component: lazy(() => import('src/views/monday/MondayBatchSync')),
          groups: [MONDAY_ADMINS_GROUP],
        },
        {
          key: 'mondayWorkspaceApp',
          path: '/monday-explorer',
          component: lazy(() => import('src/views/monday/MondayWorkspaceApp')),
          groups: [MONDAY_ADMINS_GROUP],
        },
        {
          key: 'permissions',
          path: '/permissions',
          component: lazy(() => import('src/views/permissions/index')),
          groups: [HARVEST_EMPLOYEES_GROUP],
        },
        {
          key: 'forecastRoundtableTool',
          path: '/forecast-roundtable-tool',
          component: lazy(() => import('src/views/forecastRoundtable/index')),
          groups: [HARVEST_EMPLOYEES_GROUP],
        },
        {
          key: 'estimateActuals',
          path: '/estimate-actuals',
          component: lazy(() => import('src/views/estimateActuals/index')),
          groups: [HARVEST_EMPLOYEES_GROUP],
          featureFlag: FEATURE_FLAGS.ESTIMATE_ACTUALS,
        },
        {
          key: 'forecastingV2',
          path: '/forecasting-2',
          component: lazy(() => import('src/views/forecastingV2/index')),
          groups: [processAiGroup],
        },
        {
          key: 'weeklyCall',
          path: '/weekly-call',
          component: lazy(() => import('src/views/weeklyCall/index')),
          groups: [processAiGroup],
        },
        {
          key: 'forecastRoundtableManager',
          path: '/forecast-roundtable-manager',
          component: lazy(() => import('src/views/forecastRoundtableManager/index')),
          groups: [CONSOLE_FINANCE_GROUP],
        },
        {
          key: 'forecastAdministration',
          path: '/forecast-administration',
          component: lazy(() => import('src/views/forecastAdministration/index')),
          groups: [processAiGroup],
        },
        {
          key: 'netsuiteClientMapping',
          path: '/netsuite-client-mapping',
          component: lazy(() => import('src/views/netsuiteClientMapping/index')),
          groups: [CONSOLE_FINANCE_GROUP, CONSOLE_GROWTH_GROUP, CONSOLE_ADMINS_GROUP],
        },
        {
          key: 'help',
          path: '/help',
          component: lazy(() => import('src/views/help/index')),
          featureFlag: FEATURE_FLAGS.HELP_CENTER,
        },
        {
          key: 'pbcsFileUploader',
          path: '/pbcs-file-uploader',
          component: lazy(() => import('src/views/PBCSFileUploader/index')),
          groups: [CONSOLE_FINANCE_GROUP],
        },
        {
          key: 'clientServicesDirectory',
          path: '/client-services-directory',
          groups: [HARVEST_EMPLOYEES_GROUP],
          routes: [
            {
              key: 'list',
              path: '',
              index: true,
              component: lazy(() => import('src/views/clientAttribution/index')),
              groups: [HARVEST_EMPLOYEES_GROUP],
            },
            {
              key: 'detailView',
              path: '/detail',
              component: lazy(() => import('src/views/clientAttribution/ClientServicesDetailView')),
              groups: [HARVEST_EMPLOYEES_GROUP],
            },
          ],
        },
        {
          key: 'onboardingInitiated',
          path: '/onboarding-initiated',
          component: lazy(() => import('src/views/clientAttribution/OnboardingInitiatedView')),
          groups: [HARVEST_EMPLOYEES_GROUP],
        },
        {
          key: 'clientAttributionDetail',
          path: '/client-attribution-detail',
          component: lazy(() => import('src/views/clientAttribution/ClientAttributionDetailView')),
          groups: [HARVEST_EMPLOYEES_GROUP],
        },
        {
          key: 'clientAttributionManager',
          path: '/client-attribution-manager',
          component: lazy(() => import('src/views/clientAttributionManager/index')),
          groups: [CONSOLE_CLIENT_ATTRIBUTION_GROUP],
        },
        {
          key: 'clientManager',
          path: '/client-manager',
          component: lazy(() => import('src/views/clientManager/index')),
          groups: [CONSOLE_ADMINS_GROUP],
        },
        {
          key: 'guestUserRequests',
          path: '/guest-user-requests',
          component: lazy(() => import('src/views/guestUserRequests/index')),
          groups: [CONSOLE_ADMINS_GROUP, CONSOLE_EXPERIENCE_GROUP],
        },
        {
          key: 'manageServices',
          path: '/manage-services',
          component: lazy(() => import('src/views/clientAttribution/ManageServicesPage')),
          groups: [HARVEST_EMPLOYEES_GROUP],
        },
        {
          key: 'clientOnboarding',
          path: '/client-onboarding',
          component: lazy(() => import('src/views/clientOnboarding/index')),
          groups: [HARVEST_EMPLOYEES_GROUP],
        },
        {
          key: 'clientOffboardingDashboard',
          path: '/client-offboarding-dashboard',
          groups: [CONSOLE_ADMINS_GROUP],
          routes: [
            {
              key: 'list',
              path: '',
              index: true,
              component: lazy(() => import('src/views/clientOffboardingDashboard/index')),
              groups: [CONSOLE_ADMINS_GROUP],
            },
            {
              key: 'detailView',
              path: '/detail',
              component: lazy(() => import('src/views/clientOffboardingDashboard/OffboardingDetailPage')),
              groups: [CONSOLE_ADMINS_GROUP],
            },
          ],
        },
      ],
    },
  ];

  let arrayRoutes = flattenRoutes(routeDefinitions);
  let nestedRoutes = processRoutes(JSON.parse(JSON.stringify(routeDefinitions)));
  const objectRoutes = objectify(nestedRoutes);

  return {
    routes: arrayRoutes,
    objectRoutes,
  };
};

const AppRoutes = ({ routes, objectRoutes }) => {
  const { user, hasProcessAIPermission } = useAuth();
  const { featureFlags, isLoaded: featureFlagsLoaded } = useFeatureFlags(FRONTEND_FEATURE_FLAGS, Boolean(user));

  if (!routes && !objectRoutes && !featureFlagsLoaded) {
    return <LoadingScreen />;
  }

  if (!routes || !objectRoutes) {
    const rootRoutes = getRoutes(user, hasProcessAIPermission, featureFlags);
    routes = rootRoutes.routes;
    objectRoutes = rootRoutes.objectRoutes;
  }

  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        {routes?.map((route, i) => {
          const Guard = route.guard || Fragment;
          const Layout = route.layout || Fragment;
          const Component = route.component;
          const routeProps = { path: route.path };

          return (
            <Route
              key={i}
              {...routeProps}
              element={
                <Guard route={route}>
                  <Layout {...(Layout !== Fragment && { routes: objectRoutes })}>
                    <Component route={route} />
                  </Layout>
                </Guard>
              }
            />
          );
        })}
        <Route path="*" element={<NotFoundView />} />
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
